import { useEffect, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_CONFIG, NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import type { AssetTreeNode } from '@/shared/lib/tree';
import { findNodeByKey, updateNodeChildren } from '@/shared/lib/tree';

import { useTreeExpandStore } from '@/entities/discovery';
import { EXPERIMENT_RUNS_QUERY_KEY, useExperimentRuns } from '@/entities/experiment';
import type { RunInfo } from '@/entities/experiment';

export const useExperimentExpand = () => {
  const queryClient = useQueryClient();
  const loadingKeys = useTreeExpandStore((state) => state.loadingKeys);
  const treeData = useTreeExpandStore((state) => state.treeData);
  const processedExperimentRef = useRef<string | null>(null);

  const expandingExperimentKey = useMemo(
    () => loadingKeys.find((key) => String(key).startsWith('project-exp-')),
    [loadingKeys]
  );

  const expandingExperiment = useMemo(() => {
    if (!expandingExperimentKey) return null;
    return findNodeByKey(treeData, String(expandingExperimentKey));
  }, [treeData, expandingExperimentKey]);

  const experimentData = expandingExperiment?.data as { experimentId?: string } | undefined;
  const experimentId = experimentData?.experimentId;

  const { data: runsData, isSuccess, isError } = useExperimentRuns(experimentId || '', !!experimentId);

  useEffect(() => {
    if (isSuccess && runsData && expandingExperiment && experimentId) {
      if (processedExperimentRef.current === experimentId) {
        return;
      }

      const runNodes = createRunNodes(runsData.runs);
      const currentTreeData = useTreeExpandStore.getState().treeData;

      useTreeExpandStore.getState().setTreeData(updateNodeChildren(currentTreeData, expandingExperiment.key, runNodes));

      useTreeExpandStore.getState().addLoadedNode(expandingExperiment.key);
      useTreeExpandStore.getState().addExpandedKey(expandingExperiment.key);
      useTreeExpandStore.getState().removeLoadingKey(expandingExperiment.key);

      processedExperimentRef.current = experimentId;
    }
  }, [isSuccess, runsData, expandingExperiment, experimentId]);

  useEffect(() => {
    if (isError && expandingExperiment) {
      useTreeExpandStore.getState().removeLoadingKey(expandingExperiment.key);
      useTreeExpandStore.getState().removeExpandedKey(expandingExperiment.key);

      queryClient.removeQueries({
        queryKey: [EXPERIMENT_RUNS_QUERY_KEY, experimentId],
      });

      notification.error({
        message: NOTIFICATION_MESSAGES.EXPERIMENT_RUNS_FETCH_ERROR,
        description: NOTIFICATION_MESSAGES.EXPERIMENT_RUNS_FETCH_ERROR_DESC,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
        duration: NOTIFICATION_CONFIG.DURATION.ERROR,
      });

      processedExperimentRef.current = null;
    }
  }, [isError, expandingExperiment, experimentId, queryClient]);
};

const createRunNodes = (runs: RunInfo[]): AssetTreeNode[] => {
  return runs.map((run) => {
    return {
      key: `run-${run.id}`,
      title: run.name,
      type: 'run',
      isLeaf: false, // Run 확장 시 동적으로 서브 애셋 조회
      data: {
        runId: run.id,
        runName: run.name,
        status: run.status,
        startTime: run.startTime,
        entityType: 'run',
      },
    };
  });
};
